import { useEffect } from 'react'
import {
  PublicSignupTrigger,
  trackPublicCta,
  trackPublicCtaViewed,
} from '~publish/helpers/publicTracking'
import type { JSX } from 'react'

import { Button, Dialog } from '@buffer-mono/popcorn'

import type { GuestAccountDestination } from '~publish/helpers/guestDraftHandoff'
import {
  getLoginUrlForRedirectPath,
  getSignupUrlForRedirectPath,
} from '~publish/helpers/publicAuthUrls'
import { newPost } from '~publish/legacy/routes'

export type PublicFeatureSignupRequest = Readonly<{
  feature:
    | 'alt-text'
    | 'tagging'
    | 'channel-settings'
    | 'ai-assistant'
    | 'hashtag-manager'
    | 'timezone'
  trigger: HTMLElement
}>

export function PublicFeatureSignupDialog({
  feature,
  trigger,
  onClose,
  onContinueToAccount,
  ctaPrefix = 'publish-composer',
  returnPath = newPost.route,
}: PublicFeatureSignupRequest & {
  onClose: () => void
  onContinueToAccount: (destination: GuestAccountDestination) => void
  ctaPrefix?: string
  returnPath?: string
}): JSX.Element {
  const cta = `${ctaPrefix}-${feature}-signUp-1`
  useEffect(() => {
    trackPublicCtaViewed(cta)
  }, [cta])
  return (
    <Dialog
      open
      onOpenChange={(open): void => {
        if (!open) onClose()
      }}
    >
      <Dialog.Content
        size="medium"
        onCloseAutoFocus={(event): void => {
          event.preventDefault()
          trigger.focus()
        }}
      >
        <Dialog.Header>
          <Dialog.Title>Create your Buffer account</Dialog.Title>
          <Dialog.Description>
            {feature === 'tagging'
              ? 'Create a free account to tag people in your images.'
              : feature === 'channel-settings'
                ? 'Create a free account to connect your channels and choose post types, first comments, locations and more.'
                : feature === 'ai-assistant'
                  ? 'Create a free account to use the AI Assistant and get help writing your posts.'
                  : feature === 'hashtag-manager'
                    ? 'Create a free account to save hashtag groups and add them to your posts.'
                    : feature === 'timezone'
                      ? 'Create a free account to change your time zone.'
                      : 'Create a free account to generate alt text with AI and make your images more accessible.'}
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer>
          <Button
            as="a"
            variant="secondary"
            href={getLoginUrlForRedirectPath(returnPath)}
            onClick={(event): void => {
              event.preventDefault()
              trackPublicCta(
                `${ctaPrefix}-${feature}-logIn-1`,
                PublicSignupTrigger.OTHER,
              )
              onContinueToAccount('login')
            }}
          >
            Log In
          </Button>
          <Button
            as="a"
            href={getSignupUrlForRedirectPath(returnPath)}
            onClick={(event): void => {
              event.preventDefault()
              trackPublicCta(cta, PublicSignupTrigger.OTHER)
              onContinueToAccount('signup')
            }}
          >
            Create account
          </Button>
        </Dialog.Footer>
        <Dialog.CloseButton />
      </Dialog.Content>
    </Dialog>
  )
}
